/**
 * MuiOrdersTable - Orders DataGrid table component
 *
 * OPTIMIZATION NOTES (Phase 1.1):
 * - Supports both client-side and server-side pagination modes
 * - When serverSide=true, pagination/sorting is controlled by parent
 * - When serverSide=false (default), uses DataGrid's built-in pagination
 * - Backwards compatible with existing usage
 *
 * Features:
 * - Status column with colored chips
 * - Proper date formatting
 * - Row click navigation
 * - Responsive design
 * - Server-side or client-side pagination
 *
 * @see OPTIMIZATION_PLAN.md for full details
 */

import React, { useMemo, useCallback } from "react"
import PropTypes from "prop-types"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import moment from "moment"

// MUI Components
import { Box, Chip, Typography } from "@mui/material"
import { DataGrid } from "@mui/x-data-grid"
import { styled } from "@mui/material/styles"

// MUI Icons
import LocalShippingIcon from "@mui/icons-material/LocalShipping"
import InventoryIcon from "@mui/icons-material/Inventory"
import ReceiptIcon from "@mui/icons-material/Receipt"
import LocalOfferIcon from "@mui/icons-material/LocalOffer"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import ErrorIcon from "@mui/icons-material/Error"
import PendingIcon from "@mui/icons-material/Pending"
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn"

// Custom MUI Components
import MuiStatusChip from "../../components/mui/MuiStatusChip"

// Status chip configuration
const ORDER_STATUS_CONFIG = {
  "PENDING-STOCK": {
    color: "#f1b44c",
    bgColor: "#f1b44c20",
    icon: PendingIcon,
    label: "Pending Stock",
  },
  "STOCK-OK": {
    color: "#50a5f1",
    bgColor: "#50a5f120",
    icon: CheckCircleIcon,
    label: "Stock OK",
  },
  "READY-TO-INVOICE": {
    color: "#556ee6",
    bgColor: "#556ee620",
    icon: ReceiptIcon,
    label: "Ready to Invoice",
  },
  "ON SHIPPING": {
    color: "#f1b44c",
    bgColor: "#f1b44c20",
    icon: LocalShippingIcon,
    label: "On Shipping",
  },
  INVOICED: {
    color: "#50a5f1",
    bgColor: "#50a5f120",
    icon: ReceiptIcon,
    label: "Invoiced",
  },
  OFFER: {
    color: "#556ee6",
    bgColor: "#556ee620",
    icon: LocalOfferIcon,
    label: "Offer",
  },
  SHIPPED: {
    color: "#34c38f",
    bgColor: "#34c38f20",
    icon: LocalShippingIcon,
    label: "Shipped",
  },
  RETURNED: {
    color: "#f46a6a",
    bgColor: "#f46a6a20",
    icon: AssignmentReturnIcon,
    label: "Returned",
  },
}

// Styled status chip for orders - compact
const StyledOrderStatusChip = styled(Chip)(
  ({ theme, statuscolor, bgcolor }) => ({
    backgroundColor: bgcolor || "#74788d20",
    color: statuscolor || "#74788d",
    fontWeight: 500,
    fontSize: "0.688rem",
    height: 22,
    borderRadius: 4,
    "& .MuiChip-icon": {
      color: statuscolor || "#74788d",
      fontSize: 12,
      marginLeft: 4,
    },
    "& .MuiChip-label": {
      paddingLeft: 4,
      paddingRight: 6,
    },
  })
)

// Order Status Chip Component
const OrderStatusChip = ({ status }) => {
  const { t } = useTranslation()
  const config = ORDER_STATUS_CONFIG[status] || {
    color: "#74788d",
    bgColor: "#74788d20",
    icon: PendingIcon,
    label: status,
  }

  const IconComponent = config.icon

  return (
    <StyledOrderStatusChip
      icon={<IconComponent />}
      label={t(config.label)}
      statuscolor={config.color}
      bgcolor={config.bgColor}
      size="small"
    />
  )
}

OrderStatusChip.propTypes = {
  status: PropTypes.string.isRequired,
}

// Currency formatter
const formatCurrency = (value) => {
  if (value === null || value === undefined || isNaN(value)) return "-"
  return new Intl.NumberFormat("el-GR", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
  }).format(value)
}

// Date formatter
const formatDate = (value) => {
  if (!value) return "-"
  return moment(value).format("DD/MM/YYYY")
}

/**
 * MuiOrdersTable - Orders data table with optional server-side pagination
 *
 * @param {Object} props
 * @param {Array} props.orders - Array of order objects to display
 * @param {boolean} props.loading - Loading state
 * @param {boolean} props.serverSide - Enable server-side pagination mode
 * @param {Object} props.pagination - Pagination info (for server-side mode)
 * @param {Function} props.onPageChange - Page change handler (for server-side mode)
 * @param {Function} props.onPageSizeChange - Page size change handler (for server-side mode)
 * @param {Object} props.sortModel - Sort configuration { field, direction }
 * @param {Function} props.onSortModelChange - Sort change handler (for server-side mode)
 */
const MuiOrdersTable = ({
  orders,
  loading = false,
  // Server-side pagination props
  serverSide = false,
  pagination = null,
  onPageChange = null,
  onPageSizeChange = null,
  // Server-side sorting props
  sortModel = null,
  onSortModelChange = null,
}) => {
  const navigate = useNavigate()
  const { t } = useTranslation()

  // Parse totals and prepare data
  const data = useMemo(() => {
    return orders.map((order) => {
      let parsedTotals = {}
      try {
        parsedTotals = JSON.parse(order.totals || "{}")
      } catch (e) {
        parsedTotals = {}
      }
      return {
        ...order,
        totalOrder: parsedTotals.totalOrder || 0,
        totalVat: parsedTotals.totalVat || 0,
        totalProductsAmmount: parsedTotals.totalProductsAmmount || 0,
        totalItems: parsedTotals.totalItems || 0,
      }
    })
  }, [orders])

  // DataGrid columns definition - compact
  const columns = useMemo(
    () => [
      {
        field: "id",
        headerName: t("ID"),
        width: 70,
        sortable: true,
      },
      {
        field: "created",
        headerName: t("Created"),
        width: 90,
        valueFormatter: (value) => formatDate(value),
        sortable: true,
      },
      {
        field: "name",
        headerName: t("Customer"),
        flex: 1,
        minWidth: 120,
        renderCell: (params) => (
          <Typography
            variant="body2"
            noWrap
            title={params.value}
            sx={{ fontSize: "0.813rem" }}
          >
            {params.value || "-"}
          </Typography>
        ),
        // This is the field searched server-side as 'customer_name'
        sortable: true,
      },
      {
        field: "label",
        headerName: t("Label"),
        width: 100,
        renderCell: (params) => (
          <Typography
            variant="body2"
            color="text.secondary"
            noWrap
            sx={{ fontSize: "0.813rem" }}
          >
            {params.value || "-"}
          </Typography>
        ),
        sortable: true,
      },
      {
        field: "expected_delivery_date",
        headerName: t("Delivery"),
        width: 90,
        valueFormatter: (value) => formatDate(value),
        sortable: true,
      },
      {
        field: "status",
        headerName: t("Status"),
        width: 130,
        renderCell: (params) => <OrderStatusChip status={params.value} />,
        sortable: true,
      },
      {
        field: "totalItems",
        headerName: t("Qty"),
        width: 50,
        align: "center",
        headerAlign: "center",
        sortable: false, // Computed from JSON, can't sort server-side
      },
      {
        field: "totalProductsAmmount",
        headerName: t("Subtotal"),
        width: 90,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => formatCurrency(value),
        sortable: false, // Computed from JSON
      },
      {
        field: "totalVat",
        headerName: t("VAT"),
        width: 75,
        align: "right",
        headerAlign: "right",
        valueFormatter: (value) => formatCurrency(value),
        sortable: false, // Computed from JSON
      },
      {
        field: "totalOrder",
        headerName: t("Total"),
        width: 95,
        align: "right",
        headerAlign: "right",
        renderCell: (params) => (
          <Typography
            sx={{ fontSize: "0.813rem", fontWeight: 600, color: "#111827" }}
          >
            {formatCurrency(params.value)}
          </Typography>
        ),
        sortable: false, // Computed from JSON
      },
      {
        field: "invoicedAt",
        headerName: t("Invoiced"),
        width: 90,
        valueFormatter: (value) => formatDate(value),
        sortable: true,
      },
    ],
    [t]
  )

  // Handle row click - navigate to order detail
  const handleRowClick = useCallback(
    (params) => {
      navigate(`/order-detail/${params.row.id}`)
    },
    [navigate]
  )

  // Handle pagination model change (for server-side mode)
  const handlePaginationModelChange = useCallback(
    (model) => {
      if (serverSide) {
        // model = { page: number, pageSize: number }
        if (pagination && model.page !== pagination.page) {
          onPageChange?.(model.page)
        }
        if (pagination && model.pageSize !== pagination.pageSize) {
          onPageSizeChange?.(model.pageSize)
        }
      }
    },
    [serverSide, pagination, onPageChange, onPageSizeChange]
  )

  // Handle sort model change (for server-side mode)
  const handleSortModelChangeInternal = useCallback(
    (model) => {
      if (serverSide && onSortModelChange) {
        onSortModelChange(model)
      }
    },
    [serverSide, onSortModelChange]
  )

  // Build pagination model for DataGrid
  const paginationModel = useMemo(() => {
    if (serverSide && pagination) {
      return {
        page: pagination.page,
        pageSize: pagination.pageSize,
      }
    }
    // Default for client-side mode
    return { page: 0, pageSize: 50 }
  }, [serverSide, pagination])

  // Build sort model for DataGrid
  const dataGridSortModel = useMemo(() => {
    if (serverSide && sortModel && sortModel.field) {
      return [
        {
          field: sortModel.field,
          sort: sortModel.direction?.toLowerCase() || "desc",
        },
      ]
    }
    // Default sort for client-side mode
    return [{ field: "id", sort: "desc" }]
  }, [serverSide, sortModel])

  return (
    <Box
      sx={{
        height: "calc(100vh - 240px)",
        minHeight: 400,
        width: "100%",
        bgcolor: "#fff",
        borderRadius: 1,
        border: "1px solid #e5e7eb",
        overflow: "hidden",
      }}
    >
      <DataGrid
        rows={data}
        columns={columns}
        loading={loading}
        getRowId={(row) => row.id}
        onRowClick={handleRowClick}
        // Pagination configuration
        paginationMode={serverSide ? "server" : "client"}
        rowCount={serverSide ? pagination?.total || 0 : undefined}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        pageSizeOptions={[25, 50, 100]}
        // Sorting configuration
        sortingMode={serverSide ? "server" : "client"}
        sortModel={dataGridSortModel}
        onSortModelChange={handleSortModelChangeInternal}
        // Other settings
        disableRowSelectionOnClick
        density="compact"
        localeText={{
          noRowsLabel: t("No orders found"),
          MuiTablePagination: {
            labelRowsPerPage: t("Rows"),
            labelDisplayedRows: ({ from, to, count }) =>
              `${from}-${to} / ${count !== -1 ? count : `> ${to}`}`,
          },
        }}
        sx={{
          border: "none",
          fontSize: "0.813rem",
          "& .MuiDataGrid-columnHeaders": {
            bgcolor: "#f9fafb",
            borderBottom: "1px solid #e5e7eb",
            minHeight: "40px !important",
            maxHeight: "40px !important",
          },
          "& .MuiDataGrid-columnHeader": {
            height: 40,
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 600,
            fontSize: "0.75rem",
            color: "#6b7280",
            textTransform: "uppercase",
          },
          "& .MuiDataGrid-row": {
            cursor: "pointer",
            "&:hover": {
              bgcolor: "#f3f4f6",
            },
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "1px solid #f3f4f6",
            py: 0.5,
            fontSize: "0.813rem",
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "1px solid #e5e7eb",
            bgcolor: "#f9fafb",
            minHeight: "40px !important",
          },
          "& .MuiTablePagination-root": {
            fontSize: "0.75rem",
          },
          "& .MuiDataGrid-virtualScroller": {
            minHeight: 200,
          },
          // Style for sortable column headers
          "& .MuiDataGrid-columnHeader--sortable": {
            cursor: "pointer",
          },
        }}
      />
    </Box>
  )
}

MuiOrdersTable.propTypes = {
  orders: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  // Server-side pagination props
  serverSide: PropTypes.bool,
  pagination: PropTypes.shape({
    page: PropTypes.number,
    pageSize: PropTypes.number,
    total: PropTypes.number,
    totalPages: PropTypes.number,
  }),
  onPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,
  // Server-side sorting props
  sortModel: PropTypes.shape({
    field: PropTypes.string,
    direction: PropTypes.string,
  }),
  onSortModelChange: PropTypes.func,
}

export default MuiOrdersTable
